import { useEffect, useMemo, useState } from 'react';
import { createFilterOptions } from '@mui/material';
import * as R from 'ramda';

import TextOverflowTooltip from '@atom/components/common/tooltip/TextOverflowTooltip';
import { SelectAutocomplete } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { WorkOrderField } from '@atom/types/work';
import {
  CustomFieldView,
  getTaskFieldWidth,
} from '@atom/utilities/taskUtilities';

interface Props {
  field: WorkOrderField;
  onChange: (fieldId: string, value: any) => void;
  isDisabled?: boolean;
  fieldsLoading?: Set<string>;
  isNested?: boolean;
  customFieldView: CustomFieldView;
}

const filterOptions = createFilterOptions<string>({
  matchFrom: 'any',
  limit: 250,
});

const WorkOrderEnumSingleField = ({
  field,
  onChange,
  isDisabled,
  fieldsLoading = new Set([]),
  isNested,
  customFieldView,
}: Props) => {
  const [value, setValue] = useState<string>(field.value);

  const handleChange = (event: any, option: string) => {
    const newValue = R.isNil(option) ? '' : option;

    setValue(newValue);
    onChange(field.id, newValue);
  };

  useEffect(() => {
    setValue(field.value);
  }, [field]);

  const nestedField = useMemo(() => {
    if (!field.subFields) {
      return null;
    }

    const nestedFieldIndex = R.findIndex(R.propEq(value, 'matchValue'))(
      field.subFields,
    );

    return field.subFields[nestedFieldIndex]?.nestedField;
  }, [value, field]);

  const loading = field.subFieldsTruncated && fieldsLoading.has(field.id);
  const hasSubFields = !R.isEmpty(field.subFields);

  const selectStyles =
    hasSubFields || isNested
      ? {
          marginRight: '0.5rem',
          marginBottom: '5px',
          width: '9rem',
        }
      : {
          width: getTaskFieldWidth(customFieldView),
        };

  const tooltipText = value ? `${field.title}: ${value}` : field.title;

  return (
    <>
      <div style={selectStyles}>
        <TextOverflowTooltip
          width={
            hasSubFields || isNested
              ? '9rem'
              : getTaskFieldWidth(customFieldView)
          }
          text={tooltipText}
          color={colors.neutral.dim}
          fontSize={fonts.sm}
        />
        <SelectAutocomplete
          id={field.id}
          options={field.enumeration}
          value={value}
          onChange={handleChange}
          disabled={isDisabled}
          loading={loading}
          filterOptions={filterOptions}
        />
      </div>
      {nestedField && (
        <WorkOrderEnumSingleField
          field={nestedField}
          onChange={onChange}
          isDisabled={isDisabled}
          fieldsLoading={fieldsLoading}
          isNested
          customFieldView={customFieldView}
        />
      )}
    </>
  );
};

export default WorkOrderEnumSingleField;
